import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Divider, LinearProgress, Switch, Tooltip } from "@mui/material";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import SignalCellularAlt2BarIcon from "@mui/icons-material/SignalCellularAlt2Bar";
import SignalCellularAlt1BarIcon from "@mui/icons-material/SignalCellularAlt1Bar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { RoomEvent, Track, type LocalAudioTrack, type RemoteAudioTrack, type Room } from "livekit-client";
import ParticipantAudioLevelMeter from "./ParticipantAudioLevelMeter";
import { ConnectionQuality, VideoResolution, LocalRecordingErrorCode, mapLiveKitConnectionQuality } from "./ParticipantStatusService";

const AVATAR_COLORS = [
    '#3B82F6', '#8B5CF6', '#EC4899', '#10B981',
    '#F59E0B', '#06B6D4', '#EF4444', '#6366F1',
];

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getAvatarColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getLocalRecordingErrorLabel(code?: LocalRecordingErrorCode | string): string | null {
    const trimmed = (code ?? '').trim();
    if (trimmed === '') return null;
    if (trimmed === 'start_failed') return "Local recording couldn't be started";
    if (trimmed === 'fatal') return 'Local recording failed';
    return 'Local recording issue';
}

export interface Participant {
    id: string; // participantIdentifier
    name: string;
    isHost?: boolean;
    connectionQuality: ConnectionQuality;
    videoResolution?: VideoResolution; // '480p', '720p', '1080p', '1440p', '4K'
    audioLevel?: number; // 0-1 from LiveKit
    microphoneEnabled?: boolean;
    audioTrack?: LocalAudioTrack | RemoteAudioTrack | null;
    uploadProgress?: number; // 0-100, undefined if not uploading
    uploadComplete?: boolean;
    noiseCancellationEnabled?: boolean;
    localRecordingError?: string; // present when the client's local recording failed
}

interface StudioPeopleSidebarProps {
    participants?: Participant[];
    room?: Room | null;
    recordingInProgress?: boolean;
    recordingStopped?: boolean; // True when recording stopped but uploads may still be in progress
    hostConnectionQuality?: ConnectionQuality;
    hostName?: string;
    hostVideoResolution?: VideoResolution;
    hostAudioLevel?: number;
    hostMicrophoneEnabled?: boolean;
    hostUploadProgress?: number; // Host's own upload progress (0-100)
    hostUploadComplete?: boolean;
    hostNoiseCancellationEnabled?: boolean;
    hostLocalRecordingError?: string;
    onToggleNoiseCancellation?: (participantId: string, enabled: boolean) => void;
    onToggleHostNoiseCancellation?: (enabled: boolean) => void;
}

const titleTextStyle = {
    color: 'var(--app-text-primary)',
    paddingLeft: '8px',
    paddingRight: '8px',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    fontFamily: 'Inter, sans-serif',
    userSelect: 'none' as const,
};

const ConnectionIndicator: React.FC<{ quality: ConnectionQuality }> = ({ quality }) => {
    const getColor = () => {
        if (quality === 'excellent' || quality === 'good') return '#22C55E';
        if (quality === 'fair') return '#F59E0B';
        if (quality === 'poor') return '#EF4444';
        return '#9CA3AF';
    };
    
    const getLabel = () => {
        if (quality === 'excellent') return 'Excellent';
        if (quality === 'good') return 'Good';
        if (quality === 'fair') return 'Fair';
        if (quality === 'poor') return 'Poor';
        return 'Unknown';
    };
    
    const Icon = (quality === 'excellent' || quality === 'good')
        ? SignalCellularAltIcon 
        : quality === 'fair' 
            ? SignalCellularAlt2BarIcon 
            : SignalCellularAlt1BarIcon;
    
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Icon sx={{ fontSize: 14, color: getColor() }} />
            <Typography sx={{
                fontSize: 12,
                color: 'var(--app-text-muted)',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
            }}>
                {getLabel()}
            </Typography>
        </Box>
    );
};

interface ParticipantAudioState {
    audioLevel: number;
    microphoneEnabled: boolean;
    audioTrack: RemoteAudioTrack | null;
}

interface LocalParticipantState {
    connectionQuality: ConnectionQuality;
    microphoneEnabled: boolean;
    microphoneTrack: LocalAudioTrack | null;
}

function areParticipantAudioStatesEqual(
    previousState: Record<string, ParticipantAudioState>,
    nextState: Record<string, ParticipantAudioState>,
): boolean {
    const previousKeys = Object.keys(previousState);
    const nextKeys = Object.keys(nextState);

    if (previousKeys.length !== nextKeys.length) return false;

    for (const key of nextKeys) {
        const previousEntry = previousState[key];
        const nextEntry = nextState[key];

        if (!previousEntry || !nextEntry) return false;
        if (previousEntry.microphoneEnabled !== nextEntry.microphoneEnabled) return false;
        if (previousEntry.audioTrack !== nextEntry.audioTrack) return false;
        if (Math.abs(previousEntry.audioLevel - nextEntry.audioLevel) > 0.002) return false;
    }

    return true;
}

function areLocalParticipantStatesEqual(
    previousState: LocalParticipantState,
    nextState: LocalParticipantState,
): boolean {
    return (
        previousState.connectionQuality === nextState.connectionQuality &&
        previousState.microphoneEnabled === nextState.microphoneEnabled &&
        previousState.microphoneTrack === nextState.microphoneTrack
    );
}

function getLiveParticipantMatchKey(
    participant: Participant,
    roomParticipantState: Record<string, ParticipantAudioState>,
): string | null {
    if (roomParticipantState[participant.id]) return participant.id;
    if (roomParticipantState[participant.name]) return participant.name;
    return null;
}

interface ParticipantCardProps {
    participant: Participant;
    participantIndex: number;
    showUpload: boolean;
    recordingStopped: boolean; // Only show "complete" state after recording stops
    onToggleNoiseCancellation?: (participantId: string, enabled: boolean) => void;
    onToggleHostNoiseCancellation?: (enabled: boolean) => void;
}

const ParticipantCard: React.FC<ParticipantCardProps> = ({ 
    participant, 
    showUpload,
    recordingStopped,
    onToggleNoiseCancellation,
    onToggleHostNoiseCancellation,
    participantIndex,
}) => {
    const localRecordingErrorLabel = getLocalRecordingErrorLabel(participant.localRecordingError);

    const handleNoiseCancellationToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (participant.isHost && onToggleHostNoiseCancellation) {
            onToggleHostNoiseCancellation(event.target.checked);
        } else if (onToggleNoiseCancellation) {
            onToggleNoiseCancellation(participant.id, event.target.checked);
        }
    };

    // Show toggle if it's host with handler, or non-host with handler
    const showNoiseCancellationToggle = 
        (participant.isHost && onToggleHostNoiseCancellation) || 
        (!participant.isHost && onToggleNoiseCancellation);
    
    // Only show "complete" state after recording has stopped
    // During recording, even if progress is 100%, show as "uploading" since more chunks will come
    const showAsComplete = recordingStopped && participant.uploadComplete;
    
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '12px',
                backgroundColor: 'var(--app-bg-surface)',
                border: '1px solid var(--app-border-subtle)',
                overflow: 'hidden',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                '&:hover': {
                    borderColor: 'var(--app-border-muted)',
                },
            }}
        >
            {/* Main participant info */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                p: 2,
            }}>
                {/* Initials avatar */}
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        bgcolor: getAvatarColor(participant.name),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: 'Inter, sans-serif',
                        color: '#fff',
                        flexShrink: 0,
                        letterSpacing: '0.5px',
                    }}
                >
                    {getInitials(participant.name)}
                </Box>
                
                {/* Name and connection */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                            sx={{
                                fontSize: 15,
                                fontWeight: 600,
                                color: 'var(--app-text-primary)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontFamily: 'Inter, sans-serif',
                            }}
                        >
                            {participant.name}
                        </Typography>
                        {participant.isHost && (
                            <Box
                                sx={{
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: '4px',
                                    backgroundColor: '#DBEAFE',
                                    fontSize: 11,
                                    fontWeight: 500,
                                    color: '#2563EB',
                                    fontFamily: 'Inter, sans-serif',
                                }}
                            >
                                Host
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ConnectionIndicator quality={participant.connectionQuality} />
                        {participant.videoResolution && (
                            <Tooltip title="Video resolution" arrow placement="top">
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 0.5,
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: '4px',
                                    backgroundColor: 'var(--app-bg-subtle)',
                                }}>
                                    <VideocamOutlinedIcon sx={{ fontSize: 14, color: 'var(--app-text-muted)' }} />
                                    <Typography sx={{ 
                                        fontSize: 11, 
                                        fontWeight: 500,
                                        color: 'var(--app-text-muted)',
                                        fontFamily: 'Inter, sans-serif',
                                    }}>
                                        {participant.videoResolution}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                    <ParticipantAudioLevelMeter
                        level={participant.audioLevel}
                        microphoneEnabled={participant.microphoneEnabled}
                        audioTrack={participant.audioTrack}
                    />
                </Box>
            </Box>
            
            {/* Noise Cancellation Toggle */}
            {showNoiseCancellationToggle && (
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderTop: '1px solid var(--app-border-subtle)',
                    backgroundColor: 'var(--app-bg-subtle)',
                }}>
                    <Typography sx={{ 
                        fontSize: 13, 
                        color: 'var(--app-text-primary)',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                    }}>
                        Noise Cancellation
                    </Typography>
                    <Switch
                        checked={participant.noiseCancellationEnabled ?? false}
                        onChange={handleNoiseCancellationToggle}
                        sx={{
                            width: 44,
                            height: 24,
                            padding: 0,
                            '& .MuiSwitch-switchBase': {
                                padding: 0,
                                margin: '2px',
                                transitionDuration: '200ms',
                                '&.Mui-checked': {
                                    transform: 'translateX(20px)',
                                    color: '#fff',
                                    '& + .MuiSwitch-track': {
                                        backgroundColor: '#2B6BFD',
                                        opacity: 1,
                                        border: 0,
                                    },
                                },
                            },
                            '& .MuiSwitch-thumb': {
                                boxSizing: 'border-box',
                                width: 20,
                                height: 20,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                            },
                            '& .MuiSwitch-track': {
                                borderRadius: 12,
                                backgroundColor: 'var(--app-border-subtle)',
                                opacity: 1,
                            },
                        }}
                    />
                </Box>
            )}

            {/* Local recording error */}
            {!!localRecordingErrorLabel && (
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderTop: '1px solid var(--app-border-subtle)',
                        backgroundColor: '#FEF2F2',
                        borderLeft: '3px solid #EF4444',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 13,
                            color: '#991B1B',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                        }}
                        title={participant.localRecordingError}
                    >
                        {localRecordingErrorLabel}
                    </Typography>
                </Box>
            )}
            
            {/* Upload progress - shown during/after recording */}
            {showUpload && participant.uploadProgress !== undefined && (
                <Box sx={{ 
                    px: 2, 
                    py: 1.5, 
                    borderTop: '1px solid var(--app-border-subtle)',
                    backgroundColor: 'var(--app-bg-subtle)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography sx={{ 
                            fontSize: 13, 
                            color: 'var(--app-text-primary)',
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                        }}>
                            {showAsComplete ? 'Upload complete' : 'Uploading...'}
                        </Typography>
                        {showAsComplete ? (
                            <CheckCircleIcon sx={{ fontSize: 18, color: '#22C55E' }} />
                        ) : (
                            <Typography sx={{ 
                                fontSize: 13, 
                                color: 'var(--app-text-muted)',
                                fontFamily: 'Inter, sans-serif',
                            }}>
                                {Math.round(participant.uploadProgress)}%
                            </Typography>
                        )}
                    </Box>
                    {!showAsComplete && (
                        <LinearProgress
                            variant="determinate"
                            value={participant.uploadProgress}
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: 'var(--app-border-subtle)',
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#3B82F6',
                                    borderRadius: 3,
                                },
                            }}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

const StudioPeopleSidebar: React.FC<StudioPeopleSidebarProps> = ({ 
    participants = [],
    room = null,
    recordingInProgress = false,
    recordingStopped = false,
    hostConnectionQuality = 'good',
    hostName = 'You (Host)',
    hostVideoResolution,
    hostAudioLevel = 0,
    hostMicrophoneEnabled = true,
    hostUploadProgress,
    hostUploadComplete = false,
    hostNoiseCancellationEnabled = false,
    hostLocalRecordingError,
    onToggleNoiseCancellation,
    onToggleHostNoiseCancellation,
}) => {
    const [roomParticipantState, setRoomParticipantState] = React.useState<Record<string, ParticipantAudioState>>({});
    const [localParticipantState, setLocalParticipantState] = React.useState<LocalParticipantState>({
        connectionQuality: hostConnectionQuality,
        microphoneEnabled: hostMicrophoneEnabled,
        microphoneTrack: null,
    });

    React.useEffect(() => {
        if (!room) {
            setRoomParticipantState({});
            setLocalParticipantState((previousState) => {
                const nextState: LocalParticipantState = {
                    connectionQuality: hostConnectionQuality,
                    microphoneEnabled: hostMicrophoneEnabled,
                    microphoneTrack: null,
                };

                return areLocalParticipantStatesEqual(previousState, nextState)
                    ? previousState
                    : nextState;
            });
            return;
        }

        const syncRoomState = () => {
            const nextParticipantState: Record<string, ParticipantAudioState> = {};

            room.remoteParticipants.forEach((participant) => {
                const microphonePublication = participant.getTrackPublication?.(Track.Source.Microphone);
                const nextState = {
                    audioLevel: participant.audioLevel ?? 0,
                    microphoneEnabled: participant.isMicrophoneEnabled,
                    audioTrack:
                        microphonePublication?.audioTrack?.kind === 'audio'
                            ? (microphonePublication.audioTrack as RemoteAudioTrack)
                            : null,
                };

                nextParticipantState[participant.identity] = nextState;
                if (participant.name) {
                    nextParticipantState[participant.name] = nextState;
                }
            });

            setRoomParticipantState((previousState) =>
                areParticipantAudioStatesEqual(previousState, nextParticipantState)
                    ? previousState
                    : nextParticipantState,
            );

            const microphonePublication = room.localParticipant?.getTrackPublication?.(Track.Source.Microphone);
            const nextLocalState: LocalParticipantState = {
                connectionQuality: mapLiveKitConnectionQuality(
                    room.localParticipant?.connectionQuality ?? hostConnectionQuality,
                ),
                microphoneEnabled: room.localParticipant?.isMicrophoneEnabled ?? hostMicrophoneEnabled,
                microphoneTrack:
                    microphonePublication?.audioTrack?.kind === 'audio'
                        ? (microphonePublication.audioTrack as LocalAudioTrack)
                        : null,
            };

            setLocalParticipantState((previousState) =>
                areLocalParticipantStatesEqual(previousState, nextLocalState)
                    ? previousState
                    : nextLocalState,
            );
        };

        syncRoomState();

        room.on(RoomEvent.ActiveSpeakersChanged, syncRoomState);
        room.on(RoomEvent.TrackMuted, syncRoomState);
        room.on(RoomEvent.TrackUnmuted, syncRoomState);
        room.on(RoomEvent.ConnectionQualityChanged, syncRoomState);
        room.on(RoomEvent.ParticipantConnected, syncRoomState);
        room.on(RoomEvent.ParticipantDisconnected, syncRoomState);
        room.on(RoomEvent.TrackSubscribed, syncRoomState);
        room.on(RoomEvent.TrackUnsubscribed, syncRoomState);
        room.on(RoomEvent.LocalTrackPublished, syncRoomState);
        room.on(RoomEvent.LocalTrackUnpublished, syncRoomState);
        room.on(RoomEvent.Connected, syncRoomState);
        room.on(RoomEvent.Reconnected, syncRoomState);

        return () => {
            room.off(RoomEvent.ActiveSpeakersChanged, syncRoomState);
            room.off(RoomEvent.TrackMuted, syncRoomState);
            room.off(RoomEvent.TrackUnmuted, syncRoomState);
            room.off(RoomEvent.ConnectionQualityChanged, syncRoomState);
            room.off(RoomEvent.ParticipantConnected, syncRoomState);
            room.off(RoomEvent.ParticipantDisconnected, syncRoomState);
            room.off(RoomEvent.TrackSubscribed, syncRoomState);
            room.off(RoomEvent.TrackUnsubscribed, syncRoomState);
            room.off(RoomEvent.LocalTrackPublished, syncRoomState);
            room.off(RoomEvent.LocalTrackUnpublished, syncRoomState);
            room.off(RoomEvent.Connected, syncRoomState);
            room.off(RoomEvent.Reconnected, syncRoomState);
        };
    }, [room, hostConnectionQuality, hostMicrophoneEnabled]);

    // Build display list: host first, then other participants
    const hostParticipant: Participant = {
        id: 'host',
        name: hostName,
        isHost: true,
        connectionQuality: localParticipantState.connectionQuality,
        videoResolution: hostVideoResolution,
        audioLevel: hostAudioLevel,
        microphoneEnabled: localParticipantState.microphoneEnabled,
        audioTrack: localParticipantState.microphoneTrack,
        noiseCancellationEnabled: hostNoiseCancellationEnabled,
        uploadProgress: hostUploadProgress,
        uploadComplete: hostUploadComplete,
        localRecordingError: hostLocalRecordingError,
    };

    const displayParticipants = [
        hostParticipant,
        ...participants
            .filter((participant) => !participant.isHost)
            .map((participant) => {
                const liveParticipantMatchKey = getLiveParticipantMatchKey(participant, roomParticipantState);
                const liveParticipantState = liveParticipantMatchKey
                    ? roomParticipantState[liveParticipantMatchKey]
                    : undefined;

                return {
                    participant: {
                        ...participant,
                        audioLevel: liveParticipantState?.audioLevel ?? participant.audioLevel ?? 0,
                        microphoneEnabled:
                            liveParticipantState?.microphoneEnabled ?? participant.microphoneEnabled ?? true,
                        audioTrack: liveParticipantState?.audioTrack ?? participant.audioTrack ?? null,
                    },
                    liveParticipantMatchKey,
                };
            })
            .filter(({ liveParticipantMatchKey }) => !room || !!liveParticipantMatchKey)
            .filter((entry, index, entries) => (
                entries.findIndex((candidate) => (
                    (candidate.liveParticipantMatchKey || candidate.participant.id) ===
                    (entry.liveParticipantMatchKey || entry.participant.id)
                )) === index
            ))
            .map(({ participant }) => participant),
    ];

    // Show upload progress during recording (chunks uploading in real-time) and after recording stops
    const showUpload = recordingInProgress || recordingStopped;

    return (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', p: '3px' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: '8px', gap: 1, marginTop: 1 }}>
                <Box sx={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PeopleOutlinedIcon sx={{ fontSize: 20, color: 'var(--app-text-primary)' }} />
                </Box>
                <Box sx={titleTextStyle}>People</Box>
                <Box
                    sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: '10px',
                        backgroundColor: 'var(--app-bg-subtle)',
                        fontSize: 11,
                        fontWeight: 500,
                        color: 'var(--app-text-muted)',
                        mr: 1,
                    }}
                >
                    {displayParticipants.length}
                </Box>
            </Box>

            <Divider sx={{ mx: 1 }} />

            {/* Participants list */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    px: 1,
                    pb: 1,
                    flex: 1,
                    overflow: 'auto',
                }}
            >
                {displayParticipants.map((participant, index) => (
                    <ParticipantCard
                        key={participant.id}
                        participant={participant}
                        participantIndex={index + 1}
                        showUpload={showUpload}
                        recordingStopped={recordingStopped}
                        onToggleNoiseCancellation={onToggleNoiseCancellation}
                        onToggleHostNoiseCancellation={onToggleHostNoiseCancellation}
                    />
                ))}
            </Box>

            {/* Recording/uploading status indicator */}
            {(recordingInProgress || recordingStopped) && (
                <Box
                    sx={{
                        mx: 1,
                        mb: 1,
                        p: 1.5,
                        borderRadius: '8px',
                        backgroundColor: recordingStopped ? '#FEF3C7' : '#FEF2F2',
                        border: `1px solid ${recordingStopped ? '#FCD34D' : '#FECACA'}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: recordingStopped ? '#F59E0B' : '#EF4444',
                            animation: 'pulse 1.5s ease-in-out infinite',
                            '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.5 },
                            },
                        }}
                    />
                    <Typography sx={{ 
                        fontSize: 12, 
                        color: recordingStopped ? '#92400E' : '#991B1B', 
                        fontWeight: 500 
                    }}>
                        {recordingStopped ? 'Uploading recordings...' : 'Recording in progress'}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default StudioPeopleSidebar;
